import React, { useState, useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { X, Plus, Gamepad2 } from 'lucide-react';
import LoadingSpinner from '../ui/LoadingSpinner';

interface AddGameModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (gameData: { game: string; game_type: string; icon_url?: string }) => void;
  loading: boolean;
}

const AddGameModal: React.FC<AddGameModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  loading
}) => {
  const [formData, setFormData] = useState({
    game: '',
    game_type: '',
    icon_url: ''
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const modalRef = useRef<HTMLDivElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      
      gsap.set([overlayRef.current, modalRef.current], { 
        opacity: 0,
        visibility: 'visible'
      });
      
      gsap.set(modalRef.current, { scale: 0.9, y: 50 });
      
      gsap.to(overlayRef.current, {
        opacity: 1,
        duration: 0.2
      });
      
      gsap.to(modalRef.current, {
        opacity: 1,
        scale: 1,
        y: 0,
        duration: 0.3,
        ease: 'power2.out',
        delay: 0.1
      });
    } else {
      document.body.style.overflow = '';
      
      if (modalRef.current && overlayRef.current) {
        gsap.to(modalRef.current, {
          opacity: 0,
          scale: 0.9,
          y: 50,
          duration: 0.2,
          ease: 'power2.in'
        });
        
        gsap.to(overlayRef.current, {
          opacity: 0,
          duration: 0.2,
          delay: 0.1,
          onComplete: () => {
            gsap.set([overlayRef.current, modalRef.current], { 
              visibility: 'hidden' 
            });
          }
        });
      }
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.game.trim()) {
      newErrors.game = 'Game name is required.';
    } else if (formData.game.trim().length < 2) {
      newErrors.game = 'Game name must be at least 2 characters long.';
    } else if (formData.game.trim().length > 100) {
      newErrors.game = 'Game name must be less than 100 characters.';
    }
    
    if (!formData.game_type.trim()) {
      newErrors.game_type = 'Game type is required.';
    } else if (formData.game_type.trim().length < 2) {
      newErrors.game_type = 'Game type must be at least 2 characters long.';
    } else if (formData.game_type.trim().length > 50) {
      newErrors.game_type = 'Game type must be less than 50 characters.';
    }
    
    // Validate icon URL if provided
    if (formData.icon_url.trim()) {
      try {
        new URL(formData.icon_url.trim());
      } catch {
        newErrors.icon_url = 'Please enter a valid URL for the icon.';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validateForm()) {
      return;
    }
    
    const submitData = {
      game: formData.game.trim(),
      game_type: formData.game_type.trim(),
      ...(formData.icon_url.trim() && { icon_url: formData.icon_url.trim() })
    };
    
    onSubmit(submitData);
  };

  const handleClose = () => {
    if (!loading) {
      setFormData({ game: '', game_type: '', icon_url: '' });
      setErrors({});
      onClose();
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50">
      {/* Overlay */}
      <div
        ref={overlayRef}
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        style={{ visibility: 'hidden' }}
        onClick={handleClose}
      />
      
      {/* Modal */}
      <div className="absolute inset-0 flex items-center justify-center p-4">
        <div
          ref={modalRef}
          className="bg-white dark:bg-dark-800 rounded-xl shadow-2xl border border-gray-200 dark:border-dark-700 w-full max-w-md"
          style={{ visibility: 'hidden' }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-dark-700">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-primary-100 dark:bg-primary-900/20 rounded-lg flex items-center justify-center">
                <Gamepad2 className="w-4 h-4 text-primary-600 dark:text-primary-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                Add New Game
              </h3>
            </div>
            <button
              onClick={handleClose}
              disabled={loading}
              className="p-1 hover:bg-gray-100 dark:hover:bg-dark-700 rounded-md transition-colors disabled:opacity-50"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          
          {/* Content */}
          <div className="p-6 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Game Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.game}
                onChange={(e) => handleInputChange('game', e.target.value)}
                disabled={loading}
                className={`input-field ${errors.game ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''}`}
                placeholder="Enter game name..."
                maxLength={100}
              />
              {errors.game && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                  {errors.game}
                </p>
              )}
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Game Type <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.game_type}
                onChange={(e) => handleInputChange('game_type', e.target.value)}
                disabled={loading}
                className={`input-field ${errors.game_type ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''}`}
                placeholder="e.g., RPG, Strategy, Action..."
                maxLength={50}
              />
              {errors.game_type && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                  {errors.game_type}
                </p>
              )}
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Icon URL <span className="text-gray-500">(optional)</span>
              </label>
              <input
                type="url"
                value={formData.icon_url}
                onChange={(e) => handleInputChange('icon_url', e.target.value)}
                disabled={loading}
                className={`input-field ${errors.icon_url ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''}`}
                placeholder="https://example.com/icon.png"
              />
              {errors.icon_url && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                  {errors.icon_url}
                </p>
              )}
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                If no icon is provided, a default game icon will be used.
              </p>
            </div>
            
            <div className="text-sm text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-dark-700 rounded-lg p-3">
              <p className="font-medium mb-1">Note:</p>
              <p>• The game will be created for the currently selected company</p>
              <p>• Game name and type combination must be unique</p>
            </div>
          </div>
          
          {/* Footer */}
          <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 dark:border-dark-700">
            <button
              onClick={handleClose}
              disabled={loading}
              className="btn-secondary"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={loading || !formData.game.trim() || !formData.game_type.trim()}
              className="btn-primary"
            >
              {loading ? (
                <>
                  <LoadingSpinner size="small" />
                  Creating...
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4" />
                  Create Game
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddGameModal;
